import "./PersonalDetails.scss";

import { Tab } from "@headlessui/react";
import axios, { AxiosError } from "axios";
import clsx from "clsx";
import { useAtom } from "jotai";
import { useContext, useEffect, useLayoutEffect, useState } from "react";
import { FormProvider } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { FCC } from "types";

import { MenuPartner } from "@/assets/icons/icons";
import { Button, SubmitButton } from "@/components/Interface/Button/Button";
import { HighlightPanel } from "@/components/Interface/HighlightPanel/HighlightPanel";
import { Modal } from "@/components/Interface/Modal/Modal";
import { ButtonGroupFlexBox } from "@/components/Layout/ButtonGroupFlexBox/ButtonGroupFlexBox";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import {
  AuthAndPersonalDetailsFormFields,
  CustomerTypeAndSegment,
} from "@/components/Onboarding/Steps/PersonalDetails/personalDetails.types";
import { TermsAgreementField } from "@/components/Onboarding/Steps/PersonalDetails/TermsAndConditionsField";
import { useHandleAnonymousUserSubmit } from "@/components/Onboarding/Steps/PersonalDetails/useHandleAnonymousUserSubmit";
import { useHandleUpdateCustomer } from "@/components/Onboarding/Steps/PersonalDetails/useHandleUpdateCustomer";
import { usePersonalDetailsForm } from "@/components/Onboarding/Steps/PersonalDetails/usePersonalDetailsForm";
import { Step } from "@/components/Onboarding/Steps/Step";
import { TwoFactorCodeModal } from "@/components/Portal/UserAccount/Security/TwoFactorCodeModal";
import { VerifyEmailModal } from "@/components/Welcome/VerifyEmailModal";
import { useAlert } from "@/hooks/useAlert";
import { useLogout } from "@/hooks/useLogout";
import { DataContext } from "@/provider/DataContextProvider";
import { useGetCustomerTribe, useLeaveTribe } from "@/services/api";
import {
  BasicCustomerData,
  BasicCustomerDataTwoFactorMethod,
} from "@/services/model";
import { onboardingAnalytics } from "@/utils/analytics/onboardingAnalytics";
import { API_ROOT, CUSTOMER_AUTHORIZATION } from "@/utils/apiUrls";
import { isAnonymousUserAtom } from "@/utils/atoms";
import { getEntry } from "@/utils/dataContextHelpers";
import { createLoginErrorHandler, LoginError } from "@/utils/loginErrorHandler";
import { isFlatHomeTariff } from "@/utils/tariffUtils";

import { AuthFormFields } from "./AuthFormFields";
import {
  excludedFieldsBusiness,
  excludedFieldsBusinessAnonymous,
  excludedFieldsPrivate,
  excludedFieldsPrivateAnonymous,
  PersonalDetailsFormFields,
} from "./PersonalDetailsFormFields/PersonalDetailsFormFields";
import { useCustomerData } from "./useCustomerState";

/**
 * PersonalDetails Component
 *
 * This component handles the personal details step in the onboarding process.
 * It manages form state for both private and business customers, handles
 * form submission for new and existing users, and navigates to the next step
 * upon successful submission.
 *
 * Key features:
 * - Switches between private and business customer forms
 * - Handles form state and validation
 * - Manages login and account creation in the eSIM Journey
 * - Submits user data to update or create customer profiles
 */
export const PersonalDetails: FCC = () => {
  const { dataContext, setDataContext } = useContext(DataContext);
  const navigate = useNavigate();
  const logout = useLogout();
  const { t } = useTranslation();
  const showAlert = useAlert();
  const [isAnonymousUser] = useAtom(isAnonymousUserAtom);
  const [twoFactorGrantToken, setTwoFactorGrantToken] = useState<string>();
  const [hasApiError, setHasApiError] = useState(false);
  const [shouldShowVerifyEmailModal, setShouldShowVerifyEmailModal] =
    useState(false);

  const {
    isExistingAccount,
    setIsExistingAccount,
    twoFactorMethod,
    setTwoFactorMethod,
    selectedCustomerTypeTabIndex,
    setSelectedCustomerTypeTabIndex,
    customerData,
    isLoadingCustomer,
  } = useCustomerData();

  const { data: customerTribe, refetch: refetchCustomerTribe } =
    useGetCustomerTribe({
      query: { enabled: !isAnonymousUser },
    });

  const { formMethods, formValues } = usePersonalDetailsForm();

  const [userCredentials, setUserCredentials] = useState({
    username: "",
    password: "",
  });

  // Watch form inputs
  useEffect(() => {
    const subscription = formMethods.watch((values) => {
      setUserCredentials({
        username: values.email || "",
        password: values.password || "",
      });
    });

    return () => subscription.unsubscribe();
  }, [formMethods, userCredentials]);

  // Clear emailAddress error when switching account types or when user changes email
  useEffect(() => {
    if (isExistingAccount !== null) {
      formMethods.clearErrors("email");
    }
  }, [isExistingAccount, formMethods]);

  useLayoutEffect(() => window.scrollTo({ top: 0, behavior: "smooth" }), []);

  // Check if this is a Flat Home tariff
  const isFlatHome = isFlatHomeTariff(
    dataContext.basket?.entries?.[0]?.options?.[0]?.product?.id,
  );

  const isValid = customerData && formValues && formMethods.formState.isValid;

  const defaultTarget = "/onboarding/5";

  const persistCustomerAndGoToCheckout = (
    customerData: BasicCustomerData,
    target = defaultTarget,
  ) => {
    setDataContext((prev) => ({
      ...prev,
      allowedMaxOnboardingStep: 5,
      customerData,
    }));
    navigate(target);
  };

  const { handleCustomerUpdate, isUpdatingCustomer } = useHandleUpdateCustomer(
    (customerData, target = defaultTarget) => {
      onboardingAnalytics.setCustomerType(
        customerData.customerType?.id === 2 ? "b2b" : "b2c",
      );
      onboardingAnalytics.pushPersonalData(target, "existing account");
      persistCustomerAndGoToCheckout(customerData, target);
    },
    formMethods,
    formValues,
    () => setShouldShowVerifyEmailModal(true),
  );

  const { handleAnonymousUser } = useHandleAnonymousUserSubmit(
    isExistingAccount,
    formMethods,
    formValues,
    (customerData, target) => {
      onboardingAnalytics.setCustomerType(
        customerData.customerType?.id === 2 ? "b2b" : "b2c",
      );
      onboardingAnalytics.pushPersonalData(target, "new account");
      persistCustomerAndGoToCheckout(customerData, target);
    },
    handleCustomerUpdate,
    setHasApiError,
  );

  const { mutate: leaveTribe } = useLeaveTribe();

  const isLoading = isLoadingCustomer || isUpdatingCustomer;
  const disablePersonalDetailsForm =
    isAnonymousUser && isExistingAccount === null;

  const [showTwoFactorModal, setShowTwoFactorModal] = useState(false);
  const shouldShowTwoFactorModal = showTwoFactorModal && !!twoFactorMethod;

  // Create login error handler with email field name for onboarding
  const handleLoginError = createLoginErrorHandler(
    formMethods.setError,
    t,
    showAlert,
    "email",
  );

  const [showBusinessModal, setShowBusinessModal] = useState(false);
  const tabPanelIndex = disablePersonalDetailsForm ? -1 : 0;

  // Handle tab change while preserving form data
  const handleTabChange = (index: number) => {
    // Store current form values before switching
    const currentValues = formMethods.getValues();

    // Update the tab index
    setSelectedCustomerTypeTabIndex(index);

    // Restore form values after a short delay to ensure the form fields are ready
    setTimeout(() => {
      Object.entries(currentValues).forEach(([key, value]) => {
        if (value !== undefined) {
          formMethods.setValue(
            key as keyof AuthAndPersonalDetailsFormFields,
            value,
            { shouldValidate: false, shouldDirty: true, shouldTouch: true },
          );
        }
      });
    }, 0);
  };

  const excludedPersonalFormFields = isAnonymousUser
    ? excludedFieldsPrivateAnonymous
    : excludedFieldsPrivate;

  const navigateToNext = async (
    formValues: AuthAndPersonalDetailsFormFields,
  ) => {
    const customerTypeAndCustomerSegment: CustomerTypeAndSegment = {
      // ...If we have a business customer (organizationName is set),
      ...(formValues.organizationName !== undefined &&
      selectedCustomerTypeTabIndex === 1
        ? // ... we want to create the customer as customerType: 2, meaning they are a business customer.
          { customerType: { id: 2 }, customerSegment: { id: 1 } }
        : // ... else they are a private customer with customerType: 1
          { customerType: { id: 1 }, customerSegment: { id: 2 } }),
    };

    if (
      twoFactorMethod !== null &&
      twoFactorMethod !== BasicCustomerDataTwoFactorMethod.STANDARD
    ) {
      // Trigger token request for further handling in TwoFactorCodeModal
      axios
        .post<{ twoFactorGrantToken: string }>(
          `${API_ROOT}/api-auth-server/oauth/token`,
          new URLSearchParams({
            grant_type: "password",
            username: userCredentials.username,
            password: userCredentials.password,
          }),
          {
            headers: {
              Accept: "application/json, text/plain, */*",
              "Accept-Language": "en,de;q=0.5",
              "Content-Type": "application/x-www-form-urlencoded",
              "X-Requested-With": "XMLHttpRequest",
              Authorization: CUSTOMER_AUTHORIZATION,
              "Sec-Fetch-Dest": "empty",
              "Sec-Fetch-Mode": "cors",
              "Sec-Fetch-Site": "same-origin",
              "Sec-GPC": "1",
            },
          },
        )
        .then((response) => {
          setTwoFactorGrantToken(response.data.twoFactorGrantToken);
          setShowTwoFactorModal(true);
          // Reset submission state to allow re-submission
          formMethods.reset(formValues, {
            keepErrors: true,
            keepIsSubmitted: false,
            keepDirty: false,
            keepTouched: false,
          });
        })
        .catch(
          (
            error: AxiosError<{ error_description?: string; message?: string }>,
          ) => {
            setHasApiError(true);

            // Convert AxiosError to LoginError format
            const loginError: LoginError = {
              status: error.response?.status,
              message:
                error.response?.data?.error_description ||
                error.response?.data?.message ||
                error.message,
            };

            handleLoginError(loginError);
            // Reset submission state to allow re-submission
            formMethods.reset(formValues, {
              keepErrors: true,
              keepIsSubmitted: false,
              keepDirty: false,
              keepTouched: false,
            });
          },
        );

      return;
    }

    if (isAnonymousUser) {
      await handleAnonymousUser(customerTypeAndCustomerSegment);
    }

    if (isValid) {
      handleCustomerUpdate(customerData, customerTypeAndCustomerSegment);
    }
  };

  return (
    <Step
      id="onboarding-personal-details"
      headline={t("Onboarding.sections.personal-details.header")}
      subheadline={t(
        `Onboarding.sections.personal-details.subtitle${
          getEntry(dataContext)?.esim ? "Esim" : "Sim"
        }`,
      )}
    >
      {isLoading && <LoadingSpinner />}

      {shouldShowTwoFactorModal && (
        <TwoFactorCodeModal
          userName={userCredentials.username}
          userPassword={userCredentials.password}
          setShowModal={(show) => {
            setShowTwoFactorModal(show);
            if (!show) {
              // Reset twoFactorMethod when modal is closed
              setTwoFactorMethod(null);
            }
          }}
          method={twoFactorMethod}
          twoFactorGrantToken={twoFactorGrantToken}
          type="esim_journey"
        />
      )}

      {showBusinessModal && (
        <Modal
          className="max-w-[628px]"
          hasScroll={false}
          title={
            <h2 className="flex gap-4">
              <MenuPartner
                className="flex h-12 w-12 items-end justify-end"
                fill="#0014FF"
              />
              {t(
                "Onboarding.sections.personal-details.friendsWithBenefitsPopUp.title",
              )}
            </h2>
          }
          onClose={() => {
            setShowBusinessModal(false);
            setSelectedCustomerTypeTabIndex(0);
          }}
        >
          <div>
            <div className="flex flex-col gap-4 pl-16">
              <p>
                <Trans
                  t={t}
                  i18nKey="Onboarding.sections.personal-details.friendsWithBenefitsPopUp.text1"
                />
              </p>
              <p className="font-semibold">
                <Trans
                  t={t}
                  i18nKey="Onboarding.sections.personal-details.friendsWithBenefitsPopUp.text2"
                />
              </p>
              <p className="text-sm text-primary-100">
                <Trans
                  t={t}
                  i18nKey="Onboarding.sections.personal-details.friendsWithBenefitsPopUp.text3"
                />
              </p>
            </div>
            <ButtonGroupFlexBox
              primaryButton={
                <Button
                  className="accent text-center"
                  type="button"
                  onClick={() => {
                    leaveTribe(undefined, {
                      onSuccess: () => {
                        refetchCustomerTribe();
                      },
                      onSettled: () => {
                        setShowBusinessModal(false);
                      },
                    });
                  }}
                >
                  {t(
                    "Onboarding.sections.personal-details.friendsWithBenefitsPopUp.cta2",
                  )}
                </Button>
              }
              secondaryButton={
                <Button
                  className="accent inverted text-center"
                  type="button"
                  onClick={() => {
                    setShowBusinessModal(false);
                    setSelectedCustomerTypeTabIndex(0);
                  }}
                >
                  {t(
                    "Onboarding.sections.personal-details.friendsWithBenefitsPopUp.cta1",
                  )}
                </Button>
              }
            />
          </div>
        </Modal>
      )}

      <form onSubmit={formMethods.handleSubmit(navigateToNext)}>
        <FormProvider {...formMethods}>
          {isAnonymousUser && (
            <div className="mt-8">
              <h3 className="text-secondary-100">
                {t("portal:user-account.personalDetails.loginData.title")}
              </h3>
              <p className="my-6 text-black">
                {t(
                  `Onboarding.sections.personal-details.loginSubtitle.${
                    isExistingAccount ? "existingAccount" : "newAccount"
                  }`,
                )}
              </p>
              <AuthFormFields
                isExistingAccount={isExistingAccount}
                setIsExistingAccount={setIsExistingAccount}
                setTwoFactorMethod={setTwoFactorMethod}
                hasApiError={hasApiError}
                setHasApiError={setHasApiError}
              />
            </div>
          )}

          {/* We show this form in all cases, except on 'login' mode or when there are API errors */}
          {isAnonymousUser && (isExistingAccount || hasApiError) ? null : (
            <div className="mt-10">
              {isAnonymousUser && (
                <h3
                  className={clsx(
                    "mb-6 text-secondary-100",
                    disablePersonalDetailsForm && "opacity-20",
                  )}
                >
                  {t("translation:Onboarding.sections.personal-details.header")}
                </h3>
              )}

              <Tab.Group
                selectedIndex={selectedCustomerTypeTabIndex}
                onChange={handleTabChange}
              >
                <Tab.List
                  className={clsx(disablePersonalDetailsForm && "opacity-20")}
                >
                  <Tab disabled={disablePersonalDetailsForm}>
                    {t("Onboarding.sections.personal-details.tabs.private")}
                  </Tab>
                  <Tab
                    onClick={() => {
                      if (customerTribe?.tribeId) {
                        setShowBusinessModal(true);
                      }
                    }}
                    disabled={disablePersonalDetailsForm}
                  >
                    {t("Onboarding.sections.personal-details.tabs.business")}
                  </Tab>
                </Tab.List>

                <Tab.Panels>
                  <Tab.Panel
                    id="private"
                    className="mt-6"
                    tabIndex={tabPanelIndex}
                  >
                    <PersonalDetailsFormFields
                      disableAllFields={disablePersonalDetailsForm}
                      excludedFields={excludedPersonalFormFields}
                    />
                  </Tab.Panel>
                  <Tab.Panel className="mt-6" tabIndex={tabPanelIndex}>
                    <PersonalDetailsFormFields
                      disableAllFields={disablePersonalDetailsForm}
                      excludedFields={
                        isAnonymousUser
                          ? excludedFieldsBusinessAnonymous
                          : excludedFieldsBusiness
                      }
                    />
                  </Tab.Panel>
                </Tab.Panels>
              </Tab.Group>

              <div className="h-16" />

              <HighlightPanel
                className={clsx(disablePersonalDetailsForm && "opacity-20")}
              >
                <p>{t("Onboarding.sections.personal-details.hint.text")}</p>
              </HighlightPanel>

              {isAnonymousUser && !isExistingAccount && (
                <TermsAgreementField disabled={disablePersonalDetailsForm} />
              )}
            </div>
          )}

          <ButtonGroupFlexBox
            className="nav-button-container"
            primaryButton={
              <SubmitButton
                dataTestid="continue-button"
                className="accent"
                label={t("Common.label.forward")}
              />
            }
            secondaryButton={
              <Button
                className="accent inverted"
                type="button"
                disabled={isFlatHome}
                onClick={() => navigate("../../3/rate-selection")}
              >
                {t("Common.label.back")}
              </Button>
            }
            forceButtonWidth
          />
        </FormProvider>
      </form>

      {shouldShowVerifyEmailModal && (
        <VerifyEmailModal
          email={userCredentials.username}
          password={userCredentials.password}
          alreadyAuthenticated
          handleSuccess={() => {
            setShouldShowVerifyEmailModal(false);

            if (customerData) {
              persistCustomerAndGoToCheckout(customerData, defaultTarget);
            }
          }}
          handleClose={() => {
            setShouldShowVerifyEmailModal(false);
            logout();
          }}
        />
      )}
    </Step>
  );
};
